import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { formatDate } from '../../utils';

// Strip HTML from WYSIWYG editor fields — react-pdf's <Text> can't render HTML.
// export function stripHtml(html = '') {
//     return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
// }

// In PdfSections.jsx
export function stripHtml(html) {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

const sharedStyles = StyleSheet.create({
    sectionHeading: {
        fontSize: 11,
        fontWeight: 'bold',
        marginTop: 12,
        marginBottom: 6,
        textTransform: 'uppercase',
    },
    contactLine: { marginBottom: 2 },
    badgeRow: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8 },
    badge: { marginRight: 4, marginBottom: 4 },
    entryBlock: { marginBottom: 10 },
    entryHeading: { fontWeight: 'bold', marginBottom: 2 },
    entrySummary: { color: '#333333', lineHeight: 1.4 },
});

// headingColor lets each template tint section headings to match its
// original CSS theme (e.g. Template5's blue, Template9's maroon)
// without duplicating this component per template.
export function ContactSection({ email, phone, address, headingColor }) {
    return (
        <View>
            <Text style={[sharedStyles.sectionHeading, headingColor && { color: headingColor }]}>Contact</Text>
            <Text style={sharedStyles.contactLine}>{email}</Text>
            <Text style={sharedStyles.contactLine}>{phone}</Text>
            <Text style={sharedStyles.contactLine}>{address}</Text>
        </View>
    );
}

export function BadgeListSection({ title, items, headingColor }) {
    if (!items?.length) return null;
    return (
        <View>
            <Text style={[sharedStyles.sectionHeading, headingColor && { color: headingColor }]}>{title}</Text>
            <View style={sharedStyles.badgeRow}>
                {items.map((item, i) => (
                    <Text key={i} style={sharedStyles.badge}>
                        {item.value}{i < items.length - 1 ? ',' : ''}
                    </Text>
                ))}
            </View>
        </View>
    );
}

export function SocialSection({ linkedinURL, twitterURL, githubURL, websiteURL, showSocial, headingColor }) {
    if (!showSocial || !(linkedinURL || twitterURL || githubURL || websiteURL)) return null;
    return (
        <View>
            <Text style={[sharedStyles.sectionHeading, headingColor && { color: headingColor }]}>Social</Text>
            {linkedinURL ? <Text style={sharedStyles.contactLine}>{linkedinURL}</Text> : null}
            {twitterURL ? <Text style={sharedStyles.contactLine}>{twitterURL}</Text> : null}
            {githubURL ? <Text style={sharedStyles.contactLine}>{githubURL}</Text> : null}
            {websiteURL ? <Text style={sharedStyles.contactLine}>{websiteURL}</Text> : null}
        </View>
    );
}

export function AboutSection({ about, headingColor }) {
    if (!about) return null;
    return (
        <View>
            <Text style={[sharedStyles.sectionHeading, headingColor && { color: headingColor }]}>About Me</Text>
            <Text style={sharedStyles.entrySummary}>{stripHtml(about)}</Text>
        </View>
    );
}

export function ExperienceSection({ jobData, headingColor }) {
    if (!jobData?.length) return null;
    return (
        <View>
            <Text style={[sharedStyles.sectionHeading, headingColor && { color: headingColor }]}>Experience</Text>
            {jobData.map((job, idx) => (
                <View key={idx} style={sharedStyles.entryBlock} wrap={false}>
                    <Text style={sharedStyles.entryHeading}>
                        {job.jobCompanyName} - {job.jobLocation} | {formatDate(job.jobFromDate)} - {formatDate(job.jobToDate)} | {job.jobTitle}
                    </Text>
                    <Text style={sharedStyles.entrySummary}>{stripHtml(job.jobSummary)}</Text>
                </View>
            ))}
        </View>
    );
}

export function EducationSection({ educationData, headingColor }) {
    if (!educationData?.length) return null;
    return (
        <View>
            <Text style={[sharedStyles.sectionHeading, headingColor && { color: headingColor }]}>Education</Text>
            {educationData.map((edu, idx) => (
                <View key={idx} style={sharedStyles.entryBlock} wrap={false}>
                    <Text style={sharedStyles.entryHeading}>
                        {edu.eduUniversity} | {edu.eduLocation} | {edu.eduTitle} | {formatDate(edu.eduFromDate)} - {formatDate(edu.eduToDate)}
                    </Text>
                    <Text style={sharedStyles.entrySummary}>{stripHtml(edu.eduSummary)}</Text>
                </View>
            ))}
        </View>
    );
}

export function ReferencesSection({ referenceData, headingColor }) {
    if (!referenceData?.length) return null;
    return (
        <View>
            <Text style={[sharedStyles.sectionHeading, headingColor && { color: headingColor }]}>References</Text>
            {referenceData.map((ref, idx) => (
                <View key={idx} style={sharedStyles.entryBlock} wrap={false}>
                    <Text style={sharedStyles.entryHeading}>{ref.name || ''} — {ref.title || ''}</Text>
                    <Text style={sharedStyles.entrySummary}>{ref.email || ''} | {ref.phone || ''}</Text>
                </View>
            ))}
        </View>
    );
}